import React from "react";
import CurrencyFormat from "react-currency-format";
import AutoResizeTextarea from "./AutoResizeTextarea";
import BookingVehicleSummary from "./BookingVehicleSummary";

const isEmpty = (v) => !String(v || "").trim();

/**
 * Final booking details collected after a quote is accepted.
 * Fully controlled by QuoteWidget via the `values` / `set` props.
 */
const BookingForm = ({
  values,
  set,
  price,
  truckType,
  vehicletypes = [],
  tripDistance = 0,
  tripDuration = 0,
  quotedRunners = 0,
  quotedFloors = 0,
  loading,
  apierror,
  agreeToTerms,
  onToggleTerms,
  canSubmit,
  onSubmit,
}) => {
  const needed = {
    firstname: isEmpty(values.firstname),
    lastname: isEmpty(values.lastname),
    email: isEmpty(values.email),
    mobilenumber: isEmpty(values.mobilenumber),
    collectiondate: isEmpty(values.collectiondate),
    collectiontime: isEmpty(values.collectiontime),
    description: isEmpty(values.description),
    terms: !agreeToTerms,
  };

  const labelClass = (key) => "lp2-label" + (needed[key] ? " is-empty-req" : "");
  const inputClass = (key) => "lp2-input" + (needed[key] ? " is-empty-req" : "");

  return (
    <div className="lp2-booking" id="lp2-makebooking">
      <h3>
        Schedule your trip
        {price > 0 && (
          <span className="lp2-booking-price">
            {" "}
            at{" "}
            <CurrencyFormat
              value={price / 100}
              displayType="text"
              thousandSeparator
              prefix="R"
              decimalScale={2}
            />
          </span>
        )}
      </h3>

      <BookingVehicleSummary
        truckType={truckType}
        vehicletypes={vehicletypes}
        tripDistance={tripDistance}
        tripDuration={tripDuration}
        quotedRunners={quotedRunners}
        quotedFloors={quotedFloors}
      />

      <div className="lp2-grid-2 lp2-field-group" style={{ marginTop: 16 }}>
        <div>
          <label className={labelClass("firstname")} htmlFor="lp2-firstname">
            First name<span className="lp2-req">*</span>
          </label>
          <input
            id="lp2-firstname"
            className={inputClass("firstname")}
            type="text"
            value={values.firstname}
            onChange={(e) => set({ firstname: e.target.value })}
            aria-invalid={needed.firstname || undefined}
          />
        </div>
        <div>
          <label className={labelClass("lastname")} htmlFor="lp2-lastname">
            Surname<span className="lp2-req">*</span>
          </label>
          <input
            id="lp2-lastname"
            className={inputClass("lastname")}
            type="text"
            value={values.lastname}
            onChange={(e) => set({ lastname: e.target.value })}
            aria-invalid={needed.lastname || undefined}
          />
        </div>
      </div>

      <div className="lp2-grid-2 lp2-field-group">
        <div>
          <label className={labelClass("email")} htmlFor="lp2-bk-email">
            Email<span className="lp2-req">*</span>
          </label>
          <input
            id="lp2-bk-email"
            className={inputClass("email")}
            type="email"
            value={values.email}
            onChange={(e) => set({ email: e.target.value })}
            aria-invalid={needed.email || undefined}
          />
        </div>
        <div>
          <label className={labelClass("mobilenumber")} htmlFor="lp2-bk-mobile">
            Mobile number<span className="lp2-req">*</span>
          </label>
          <input
            id="lp2-bk-mobile"
            className={inputClass("mobilenumber")}
            type="tel"
            value={values.mobilenumber}
            onChange={(e) => set({ mobilenumber: e.target.value })}
            aria-invalid={needed.mobilenumber || undefined}
          />
        </div>
      </div>

      <div className="lp2-grid-2 lp2-dt-grid lp2-field-group">
        <div>
          <label className={labelClass("collectiondate")} htmlFor="lp2-bk-date">
            Collection date<span className="lp2-req">*</span>
          </label>
          <input
            id="lp2-bk-date"
            className={inputClass("collectiondate")}
            type="date"
            value={values.collectiondate}
            onChange={(e) => set({ collectiondate: e.target.value })}
            aria-invalid={needed.collectiondate || undefined}
          />
        </div>
        <div>
          <label className={labelClass("collectiontime")} htmlFor="lp2-bk-time">
            Collection time<span className="lp2-req">*</span>
          </label>
          <input
            id="lp2-bk-time"
            className={inputClass("collectiontime")}
            type="time"
            value={values.collectiontime}
            onChange={(e) => set({ collectiontime: e.target.value })}
            aria-invalid={needed.collectiontime || undefined}
          />
        </div>
      </div>

      <div className="lp2-field-group">
        <label className={labelClass("description")} htmlFor="lp2-bk-desc">
          Instructions<span className="lp2-req">*</span>
        </label>
        <AutoResizeTextarea
          id="lp2-bk-desc"
          className={
            "lp2-input lp2-input--area" + (needed.description ? " is-empty-req" : "")
          }
          value={values.description}
          placeholder="Describe what we are moving - names, sizes and quantity. Add as much detail as possible."
          onChange={(e) => set({ description: e.target.value })}
          aria-invalid={needed.description || undefined}
        />
      </div>

      <div
        className={
          "lp2-terms" +
          (agreeToTerms ? " is-agreed" : "") +
          (needed.terms ? " is-empty-req" : "")
        }
        role="checkbox"
        tabIndex={0}
        aria-checked={agreeToTerms}
        onClick={onToggleTerms}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggleTerms();
          }
        }}
      >
        <span className="lp2-terms-box" aria-hidden="true">
          {agreeToTerms && <i className="mdi mdi-check" />}
        </span>
        <span>
          <span className="lp2-terms-title">
            I accept the Terms and Conditions
          </span>
          <span className="lp2-terms-sub">
            I agree to the{" "}
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              Terms and Conditions
            </a>{" "}
            and the limits of liability.
          </span>
        </span>
      </div>

      <BookingVehicleSummary
        truckType={truckType}
        vehicletypes={vehicletypes}
        tripDistance={tripDistance}
        tripDuration={tripDuration}
        quotedRunners={quotedRunners}
        quotedFloors={quotedFloors}
      />
      {loading && <p className="lp2-help">Searching for a driver...</p>}
      {!loading && apierror ? <p className="lp2-error">{apierror}</p> : null}

      <button
        id="lp2-booking-submit"
        type="button"
        className="lp2-btn lp2-btn--primary lp2-btn--block lp2-btn--lg"
        onClick={onSubmit}
        disabled={!canSubmit || loading}
      >
        {loading ? (
          <span className="lp2-spin" aria-hidden="true" />
        ) : (
          <>
            Confirm booking
            <i className="mdi mdi-arrow-right" aria-hidden="true" />
          </>
        )}
      </button>
    </div>
  );
};

export default BookingForm;
